/**
 * Boot do banco local-first. Garante a config singleton, pré-carrega o CATÁLOGO
 * INICIAL de alimentos (conteúdo padrão da ferramenta, em todos os ambientes) e
 * aplica defaults (grupo + ilustração) nos que ainda não têm.
 *
 * Em dev, cria também um paciente de exemplo (conveniência de desenvolvimento).
 */
import { db } from "../schema.dexie";
import { DEMO_FLAG, DEMO_VERSION, semearDemo } from "../seedDemo";
import { catalogoInicial } from "../lib/catalogoInicial";
import { catalogoMusicalInicial } from "../music/catalogoInicial";
import { catalogoCorporalInicial } from "../body/catalogoInicial";
import { POPULARES, normalizarNome } from "../lib/catalogo";
import { refIlustracaoPorNome } from "../lib/catalogoImagens";
import { CONFIG_PADRAO, type Alimento } from "../types";

const DEMO_ATIVO = import.meta.env.DEV; // massa de demonstração só em desenvolvimento

/** nome normalizado → grupo, a partir do catálogo de populares. */
const GRUPO_POR_NOME = new Map(POPULARES.map((p) => [normalizarNome(p.nome), p.grupo]));

/**
 * Pré-carrega o catálogo padrão. Idempotente e seguro pra quem já usa: só adiciona
 * o que ainda não existe (dedup por nome normalizado), então não duplica nem
 * sobrescreve o que a família cadastrou.
 */
async function preencherCatalogoInicial(): Promise<void> {
  const existentes = await db.alimento.toArray();
  const nomes = new Set(existentes.map((a) => normalizarNome(a.nome)));
  const novos = catalogoInicial().filter((a) => !nomes.has(normalizarNome(a.nome)));
  if (novos.length) await db.alimento.bulkAdd(novos);
}

/**
 * Pré-carrega o catálogo musical padrão (Compasso). Idempotente: só adiciona o
 * que ainda não existe (dedup por id estável `mus-…`), sem duplicar nem sobrescrever
 * o que a família cadastrou.
 */
async function preencherCatalogoMusical(): Promise<void> {
  const existentes = new Set((await db.experiencia.toArray()).map((e) => e.id));
  const novos = catalogoMusicalInicial().filter((e) => !existentes.has(e.id));
  if (novos.length) await db.experiencia.bulkAdd(novos);
}

/**
 * Pré-carrega o catálogo psicomotor padrão (Gingado). Idempotente: só adiciona o
 * que ainda não existe (dedup por id estável `psm-…`), sem duplicar nem sobrescrever
 * o que a família cadastrou.
 */
async function preencherCatalogoCorporal(): Promise<void> {
  const existentes = new Set((await db.atividade.toArray()).map((a) => a.id));
  const novos = catalogoCorporalInicial().filter((a) => !existentes.has(a.id));
  if (novos.length) await db.atividade.bulkAdd(novos);
}

/**
 * Backfill da config singleton: garante os campos do Compasso e do Gingado em bases
 * antigas (criadas antes desses módulos). Nunca sobrescreve valor já definido.
 */
async function preencherConfigMusica(): Promise<void> {
  const cfg = await db.config.get("singleton");
  if (!cfg) return;
  const patch: Partial<typeof cfg> = {};
  if (cfg.musica_rotina_dias === undefined) patch.musica_rotina_dias = CONFIG_PADRAO.musica_rotina_dias;
  if (cfg.musica_escolha_qtd === undefined) patch.musica_escolha_qtd = CONFIG_PADRAO.musica_escolha_qtd;
  if (cfg.elo_lembrete_dias === undefined) patch.elo_lembrete_dias = CONFIG_PADRAO.elo_lembrete_dias;
  if (cfg.gingado_rotina_dias === undefined) patch.gingado_rotina_dias = CONFIG_PADRAO.gingado_rotina_dias;
  if (cfg.gingado_escolha_qtd === undefined) patch.gingado_escolha_qtd = CONFIG_PADRAO.gingado_escolha_qtd;
  // Perfil profissional e Agenda (ADR-22): nascem desligados em bases antigas.
  if (cfg.perfil_profissional === undefined) patch.perfil_profissional = CONFIG_PADRAO.perfil_profissional;
  if (cfg.agenda_ativa === undefined) patch.agenda_ativa = CONFIG_PADRAO.agenda_ativa;
  if (cfg.clinica_id === undefined) patch.clinica_id = CONFIG_PADRAO.clinica_id;
  if (cfg.agenda_provedor === undefined) patch.agenda_provedor = CONFIG_PADRAO.agenda_provedor;
  if (Object.keys(patch).length) await db.config.update("singleton", patch);
}

/**
 * Backfill idempotente nos alimentos do usuário: preenche grupo e ilustração que
 * estejam VAZIOS quando o nome é reconhecível. Nunca sobrescreve escolha do usuário.
 */
async function preencherDefaults(): Promise<void> {
  const todos = await db.alimento.toArray();
  await Promise.all(
    todos.map((a) => {
      const patch: Partial<Alimento> = {};
      if (!a.grupo) {
        const g = GRUPO_POR_NOME.get(normalizarNome(a.nome));
        if (g) patch.grupo = g;
      }
      if (!a.fotos || a.fotos.length === 0) {
        const ref = refIlustracaoPorNome(a.nome);
        if (ref) patch.fotos = [ref];
      }
      return Object.keys(patch).length ? db.alimento.update(a.id, patch) : Promise.resolve();
    }),
  );
}

export async function bootDb(): Promise<void> {
  await db.garantirConfig();
  await preencherConfigMusica();

  // DEV: massa de DEMONSTRAÇÃO. Limpa tudo e popula 1× por DEMO_VERSION (re-semeia ao
  // bumpar a versão). A demo é a fonte da biblioteca — não mistura o catálogo padrão,
  // pra não duplicar experiências musicais. Recarregar sob demanda: Config (dev).
  if (DEMO_ATIVO) {
    if (localStorage.getItem(DEMO_FLAG) !== DEMO_VERSION) {
      await semearDemo();
      localStorage.setItem(DEMO_FLAG, DEMO_VERSION);
    }
    await preencherDefaults(); // dá ilustrações do catálogo aos alimentos da demo
    // a demo cobre Pitada/Compasso/Elo; o Gingado usa o catálogo padrão (idempotente).
    await preencherCatalogoCorporal();
    return;
  }

  // Produção: catálogo padrão (sem dados fictícios).
  await preencherCatalogoInicial();
  await preencherCatalogoMusical();
  await preencherCatalogoCorporal();
  await preencherDefaults();
}
